import {
    BudgetBillingConfigAttributes,
    BudgetMinutePriceAttributes,
    BudgetContractABTAttributes,
    BudgetBonusAttributes,
    BudgetBaselineAttributes,
    BudgetCalculationResultAttributes,
    BudgetForecastVolumeAttributes,
    BudgetWarningAckAttributes,
} from '@callmart/types';
import { extractBlobErrorMessage } from '../utils/fileUtils';
import api from './index';

const BASE = '/budget';

export interface BillingConfigWithRelations extends BudgetBillingConfigAttributes {
    minutePrices: BudgetMinutePriceAttributes[];
    contractABTs: BudgetContractABTAttributes[];
    bonuses: BudgetBonusAttributes[];
    // CALL-745: present on the version-scoped read (per-version scenario data) but omitted by the
    // global read (getGlobalBillingConfigs), which serves version-agnostic master data only.
    forecastVolumes?: BudgetForecastVolumeAttributes[];
    // CALL-745: SiS/Service is derived from the Working Unit on the server and returned on the
    // response (read-only); it is no longer a column on the billing config.
    sisClassification: string | null;
    partnerName: string | null;
    locationName: string | null;
    locationId: number | null;
    workingUnitName: string;
    // CALL-820: whether the working unit still has an active Handled-Contacts strategic volume with
    // reports — false means "Daten neu laden" finds nothing for this row (the Kairo case behind
    // CALL-819). Absent on snapshot reads (frozen history carries no live source status) = unknown.
    hasSourceVolume?: boolean;
    workingUnitLocation: {
        id: number;
        worktypeLocationId: number;
        workingUnitId: number;
        workingUnit: {
            id: number;
            worktypeId: number;
            channelId: number;
            taskId: number;
            shortName: string | null;
            budgetSiSClassification?: string | null;
            worktype: { id: number; worktype: string } | null;
            channel: { id: number; name: string } | null;
            task: { id: number; name: string } | null;
        };
        worktypeLocation: {
            id: number;
            locationId: number;
        } | null;
    } | null;
}

export interface BudgetCalculationResultWithConfig extends BudgetCalculationResultAttributes {
    billingConfig: {
        id: number;
        costCenter: string;
        prLabel: string;
        // CALL-745: derived from the Working Unit server-side; null when unclassified.
        sisClassification: string | null;
        serviceFactor: number;
        flatFee: boolean;
        partnerName: string | null;
        locationName: string | null;
        locationId: number | null;
        workingUnitName: string;
        workingUnitLocation: BillingConfigWithRelations['workingUnitLocation'];
    } | null;
}

export interface BudgetBaseline extends BudgetBaselineAttributes {}

export interface BudgetSummaryMonth {
    month: string;
    budget: number;
    // CALL-756: components of totalCost (totalCost === handlingCost + onTopIncluded) so the
    // grid can render the Handling/On Top breakdown straight from the backend value.
    handlingCost: number;
    onTopIncluded: number;
    totalCost: number;
    delta: number;
    onTopRisks: number;
    deltaInclRisks: number;
}

export type BudgetCalculationSummary = BudgetSummaryMonth[];

export interface BudgetCalculateResponse {
    message: string;
    resultCount: number;
}

// CALL-745: the read endpoint stays version-scoped (it returns the global config joined with
// this version's forecast volumes, or the frozen snapshot payload), while config/price writes
// are global (no version).
export const getBillingConfigs = async (versionId: number): Promise<BillingConfigWithRelations[]> => {
    return api.get(`${BASE}/versions/${versionId}/billing-configs`).then((res) => res.data);
};

// Global master config + prices/ABT/bonuses (no version, no forecast) for the config & prices
// pages. CALL-758: scoped to a bucket — the page passes the selected bucketId so the grids show
// only that bucket's rows (omitting it returns every bucket's configs).
export const getGlobalBillingConfigs = async (bucketId?: number): Promise<BillingConfigWithRelations[]> => {
    return api.get(`${BASE}/billing-configs`, { params: bucketId != null ? { bucketId } : {} }).then((res) => res.data);
};

/**
 * One combination that is active in Strategic Volume but has no config in the requested bucket.
 *
 * Combinations whose HEMT location cannot be matched are omitted by the endpoint — they could not be
 * identified here and nothing downstream could price or report them.
 */
export interface MissingBillingConfigRow {
    workingUnitLocationId: number;
    locationId: number;
    partnerName: string | null;
    locationName: string | null;
    worktypeName: string | null;
    channelName: string | null;
    taskName: string | null;
}

// CALL-780: the "Complete missing entries" preview. The rows arrive fully labelled and sorted — the
// page's own working-unit-location list is served with `hasTask: true` and cannot describe every
// location an active Strategic Volume may point at.
export const getMissingBillingConfigs = async (bucketId: number): Promise<MissingBillingConfigRow[]> => {
    return api.get(`${BASE}/billing-configs/missing`, { params: { bucketId } }).then((res) => res.data);
};

// CALL-820: the cheap count behind the "Complete missing entries" badge — answered from the DB alone
// (no HEMT round-trip), so it may exceed the dialog's row count when combinations with an
// unresolvable location get dropped from the full listing.
export const getMissingBillingConfigsCount = async (bucketId: number): Promise<number> => {
    return api
        .get(`${BASE}/billing-configs/missing`, { params: { bucketId, countOnly: 1 } })
        .then((res) => Number(res.data?.count ?? 0));
};

export const createBillingConfig = async (data: Record<string, unknown>) => {
    return api.post(`${BASE}/billing-configs`, data).then((res) => res.data);
};

// CALL-780: versionId is the bucket's draft, so the new rows get their ABT and forecast seeded from
// Strategic Volume like an import does. Optional — without a draft only the (global) ABT is seeded.
export const bulkCreateBillingConfigs = async (configs: Record<string, unknown>[], versionId?: number) => {
    return api.post(`${BASE}/billing-configs/bulk`, { configs, versionId }).then((res) => res.data);
};

/** One config's changed forecast months. `value: null` clears a manual override for that month. */
export interface ForecastVolumeEdit {
    billingConfigId: number;
    forecastVolumeEdits: Array<{ month: string; value: number | null }>;
}

// CALL-779: the Overview's Handlingdaten grid saves its pending edits together, in one transaction.
// Month-level semantics: only the months the user edited, `value: null` clearing one.
export const updateForecastVolumesBulk = async (versionId: number, configs: ForecastVolumeEdit[]) => {
    return api.put(`${BASE}/versions/${versionId}/forecast-volumes/bulk`, { configs }).then((res) => res.data);
};

export interface ImportBillingConfigsResponse {
    created: BillingConfigWithRelations[];
    createdCount: number;
    updatedCount: number;
    warnings: string[];
    monthColumns: string[];
    rowsParsed: number;
}

export const importBillingConfigs = async (versionId: number, file: File): Promise<ImportBillingConfigsResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${BASE}/versions/${versionId}/billing-configs/import`, formData).then((res) => res.data);
};

// CALL-750: sample import template (global, version-agnostic) generated server-side from the same
// header constants the parser keys off, so it always matches what the import accepts. 'simple'
// returns the values-less variant (config master columns only).
export const downloadBillingConfigImportTemplate = async (variant: 'full' | 'simple' = 'full'): Promise<Blob> => {
    const query = variant === 'simple' ? '?format=simple' : '';
    try {
        const res = await api.get(`${BASE}/billing-configs/import-template${query}`, { responseType: 'blob' });
        return res.data;
    } catch (error) {
        const message = await extractBlobErrorMessage(error);
        if (message && error instanceof Error) error.message = message;
        throw error;
    }
};

export const updateBillingConfig = async ({ id, ...data }: { id: number; [key: string]: unknown }) => {
    return api.patch(`${BASE}/billing-configs/${id}`, data).then((res) => res.data);
};

export const deleteBillingConfig = async (id: number) => {
    return api.delete(`${BASE}/billing-configs/${id}`).then((res) => res.data);
};

/** One edited month cell. `null` is a cleared cell — the backend deletes that month. */
export interface MonthEdit {
    month: string;
    value: number | null;
}

/** One config's share of a page's pending edits. Every part is optional: a save that only touched
 *  Bonus cells carries `bonusEdits` and nothing else. */
export interface BillingConfigEdit {
    id: number;
    fields?: Record<string, unknown>;
    minutePriceEdits?: MonthEdit[];
    contractABTEdits?: MonthEdit[];
    bonusEdits?: MonthEdit[];
}

// CALL-779: one request for a whole page of grid edits, applied in one transaction. Month-level
// semantics, unlike updateBillingConfig above: a month absent from the payload keeps its stored value,
// and only `value: null` deletes one.
export const updateBillingConfigsBulk = async (configs: BillingConfigEdit[]): Promise<BillingConfigWithRelations[]> => {
    return api.patch(`${BASE}/billing-configs/bulk`, { configs }).then((res) => res.data);
};

export const calculateBudget = async (versionId: number): Promise<BudgetCalculateResponse> => {
    return api.post(`${BASE}/versions/${versionId}/calculate`).then((res) => res.data);
};

export const getCalculationResults = async (versionId: number): Promise<BudgetCalculationResultWithConfig[]> => {
    return api.get(`${BASE}/versions/${versionId}/results`).then((res) => res.data);
};

export const getCalculationSummary = async (versionId: number): Promise<BudgetCalculationSummary> => {
    return api.get(`${BASE}/versions/${versionId}/summary`).then((res) => res.data);
};

/** CALL-779: month EDITS, not the whole series — `value: null` clears a month and an absent month is left
 *  alone. This endpoint used to destroy-and-replace, so an incomplete series deleted real budget. */
export const updateBaselines = async (versionId: number, baselineEdits: MonthEdit[]) => {
    return api.put(`${BASE}/versions/${versionId}/baselines`, { baselineEdits }).then((res) => res.data);
};

export interface RefreshSourceDataResponse {
    message: string;
    updatedConfigs: number;
    // CALL-820: configs whose working unit yielded no source data — orphaned rows the reload can
    // never fill (ids; the page maps them to row labels from its own configs cache).
    orphanedConfigIds: number[];
}

export const refreshSourceData = async (versionId: number): Promise<RefreshSourceDataResponse> => {
    return api.post(`${BASE}/versions/${versionId}/refresh-source-data`).then((res) => res.data);
};

// CALL-816: persisted acknowledgements of the Overview's completeness warning.
export const getWarningAcks = async (versionId: number): Promise<BudgetWarningAckAttributes[]> => {
    return api.get(`${BASE}/versions/${versionId}/warning-acks`).then((res) => res.data);
};

export const saveWarningAck = async (
    versionId: number,
    billingConfigId: number,
    missingMonths: string[]
): Promise<BudgetWarningAckAttributes> => {
    return api
        .put(`${BASE}/versions/${versionId}/billing-configs/${billingConfigId}/warning-ack`, { missingMonths })
        .then((res) => res.data);
};

export const deleteWarningAck = async (versionId: number, billingConfigId: number): Promise<{ success: boolean }> => {
    return api
        .delete(`${BASE}/versions/${versionId}/billing-configs/${billingConfigId}/warning-ack`)
        .then((res) => res.data);
};
